import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({ value, onChange }) => {
  const [raw, setRaw] = useState(value);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRaw(value);
    setError(null);
  }, [value]);

  const handleRawChange = (newRaw: string) => {
    setRaw(newRaw);
    try {
      JSON.parse(newRaw);
      setError(null);
      onChange(newRaw);
    } catch (e: any) {
      setError(e.message || 'JSON inválido');
    }
  };

  let parsed: any;
  let isArray = false;
  let isObjectArray = false;
  let isStringArray = false;

  try {
    parsed = JSON.parse(raw || '[]');
    isArray = Array.isArray(parsed);
    isStringArray = isArray && parsed.every((item: any) => typeof item === 'string');
    isObjectArray = isArray && !isStringArray && parsed.every((item: any) => typeof item === 'object' && item !== null && !Array.isArray(item));
  } catch {
    parsed = null;
  }

  if (!isArray || (!isObjectArray && !isStringArray)) {
    return (
      <div className="space-y-2">
        <textarea
          value={raw}
          onChange={(e) => handleRawChange(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-xs"
          placeholder='[{ "titulo": "..." }]'
        />
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-600">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        <p className="text-xs text-gray-500">Este campo requiere un JSON válido. Puedes editarlo directamente.</p>
      </div>
    );
  }

  const updateValue = (newParsed: any[]) => {
    const json = JSON.stringify(newParsed, null, 2);
    setRaw(json);
    setError(null);
    onChange(json);
  };

  // Editor de array de strings
  if (isStringArray) {
    const items: string[] = parsed;
    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[index] = e.target.value;
                updateValue(next);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs"
            />
            <button
              type="button"
              onClick={() => updateValue(items.filter((_, i) => i !== index))}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => updateValue([...items, ''])}
          className="flex items-center gap-2 text-xs text-purple-700 hover:text-purple-800 font-medium"
        >
          <Plus className="w-4 h-4" />
          Agregar item
        </button>
      </div>
    );
  }

  // Editor de array de objetos
  const items: Record<string, any>[] = parsed;
  const keys = items.length > 0 ? Object.keys(items[0]) : [];

  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-xs text-gray-500">No hay items. Agrega uno.</p>}
      {items.map((item, index) => (
        <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => updateValue(items.filter((_, i) => i !== index))}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {keys.map((key) => (
              <div key={key} className={key === 'descripcion' || key === 'testimonio' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">{key}</label>
                {key === 'descripcion' || key === 'testimonio' ? (
                  <textarea
                    value={item[key] ?? ''}
                    onChange={(e) => {
                      const next = [...items];
                      next[index] = { ...next[index], [key]: e.target.value };
                      updateValue(next);
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs"
                  />
                ) : (
                  <input
                    type="text"
                    value={item[key] ?? ''}
                    onChange={(e) => {
                      const next = [...items];
                      next[index] = { ...next[index], [key]: e.target.value };
                      updateValue(next);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          const newItem = keys.length > 0
            ? Object.fromEntries(keys.map((k) => [k, '']))
            : { titulo: '', descripcion: '' };
          updateValue([...items, newItem]);
        }}
        className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
      >
        <Plus className="w-4 h-4" />
        Agregar item
      </button>
    </div>
  );
};
